import {
  numberSchema,
  positiveNumberSchema,
  nonNegativeNumberSchema
} from 'common/form/validationSchema/number';
import * as Yup from 'yup';
import { convertTimeUnitValuesToMinutes } from 'modules/calendar/utils/convertTimeUnitValuesToMinutes';
import { AvailabilityMethod } from 'generated-graphql/graphql';
import { isRoundRobinMethod } from 'modules/calendar/utils/isRoundRobinMethod';
import { validateParticipantsForMethod } from 'modules/calendar/utils/validateParticipantsForMethod';
import { getParticipantStats } from 'modules/calendar/utils/getParticipantStats';
import { getTemplateEditBlockedReason } from 'modules/calendar/utils/getTemplateEditBlockedReason';
import { toEditableOrganizer } from 'modules/calendar/utils/toEditableOrganizer';
import { UseUserCalendarReturnData } from 'modules/calendar/hooks/useUserCalendar';
import { UserCalendarStatus } from 'generated-graphql/types';

export enum MeetingLocation {
  Video = 'Video/phone conference',
  InPerson = 'In person'
}

export enum MeetingUnit {
  Minutes = 'minutes',
  Hours = 'hours',
  Days = 'days'
}

const meetingAvailabilitySchema = Yup.array()
  .of(
    Yup.array()
      .of(Yup.date().required())
      .length(2, 'Enter a start and end time')
      .required()
      .test(
        'end-after-start',
        'End time must be after the start time',
        (range) => (range?.length === 2 ? range[1] > range[0] : true)
      )
  )
  .test('no-overlap', "Times for the same day can't overlap", (ranges) => {
    // A half-entered range arrives as [null, Date]; comparing it throws past yup
    // and blocks submit with nothing shown.
    const ordered = [...(ranges ?? [])]
      .filter(
        (range) =>
          range?.length === 2 &&
          range.every(
            (bound) => bound instanceof Date && !Number.isNaN(bound.getTime())
          )
      )
      .sort((a, b) => a[0].getTime() - b[0].getTime());

    return ordered.every(
      (range, i) => i === 0 || range[0] >= ordered[i - 1][1]
    );
  });
export type MeetingAvailability = Yup.InferType<
  typeof meetingAvailabilitySchema
>;

const DESCRIPTION_MAX_LENGTH = 8192;

const SCHEME = /\b([a-z][a-z0-9+.-]*):\/\//gi;
const BARE_WWW = /(?<![\w/@.-])www\./i;

const hasOnlyHttpsLinks = (description: string) =>
  [...description.matchAll(SCHEME)].every(
    ([, scheme]) => scheme.toLowerCase() === 'https'
  ) && !BARE_WWW.test(description);

const AVAILABILITY_FIELDS = [
  'mondayAvailability',
  'tuesdayAvailability',
  'wednesdayAvailability',
  'thursdayAvailability',
  'fridayAvailability',
  'saturdayAvailability',
  'sundayAvailability'
] as const;

const meetingTimeValueSchema =
  positiveNumberSchema.required('Value is required');
const meetingTimeUnitSchema = Yup.mixed<MeetingUnit>()
  .oneOf(Object.values(MeetingUnit))
  .required();

export const calendarMeetingTemplateSchema = Yup.object({
  title: Yup.string().trim().required('Enter a template title'),
  description: Yup.string()
    .optional()
    .max(
      DESCRIPTION_MAX_LENGTH,
      `The description must have ${DESCRIPTION_MAX_LENGTH} characters or fewer`
    )
    .test('https-links', 'Links must start with "https://"', (value) =>
      value ? hasOnlyHttpsLinks(value) : true
    ),
  location: Yup.mixed<MeetingLocation>()
    .oneOf(Object.values(MeetingLocation))
    .required(),
  locationDetails: Yup.string()
    .optional()
    .test(
      'location-details-required',
      'A location is required for an in-person meeting',
      function (value) {
        return this.parent.location === MeetingLocation.InPerson
          ? !!value?.trim()
          : true;
      }
    ),
  durationValue: meetingTimeValueSchema.test(
    'duration-max',
    'Duration must be at most 24 hours',
    function (value) {
      const valueInMinutes = convertTimeUnitValuesToMinutes(
        value,
        this.parent.durationUnit
      );
      return valueInMinutes <= 1440;
    }
  ),
  durationUnit: meetingTimeUnitSchema,
  schedulingMethod: Yup.mixed<AvailabilityMethod>()
    .oneOf(Object.values(AvailabilityMethod))
    .default(AvailabilityMethod.Collective)
    .required(),
  // `owner` is hidden, so panel rules have to report on `participants` to be seen.
  owner: Yup.string().optional(),
  participants: Yup.array()
    .of(Yup.string())
    .test('no-duplicate-emails', function (value) {
      const userCalendars: UseUserCalendarReturnData =
        this.options?.context?.userCalendars ?? [];
      const calendarIds = new Set(
        [this.parent.owner, ...(value ?? [])].filter(Boolean)
      );
      const seenEmails = new Set<string>();

      for (const id of calendarIds) {
        const email = userCalendars.find(
          (calendar) => calendar.id === id
        )?.email;
        const key = email?.trim().toLowerCase();

        if (!email || !key) {
          continue;
        }

        if (seenEmails.has(key)) {
          return this.createError({
            message: `${email} is already a participant on this meeting`
          });
        }

        seenEmails.add(key);
      }

      return true;
    })
    .test('panel-for-method', function (value) {
      const userCalendars: UseUserCalendarReturnData =
        this.options?.context?.userCalendars ?? [];
      const method = this.parent.schedulingMethod;

      // Saving drops them server-side, and re-sending an unreachable calendar is
      // rejected.
      const unreachable = (value ?? [])
        .map((id) => userCalendars.find((calendar) => calendar.id === id))
        .filter(
          (calendar) =>
            calendar && calendar.status !== UserCalendarStatus.Connected
        );

      if (unreachable.length > 0) {
        return this.createError({
          message: `${unreachable
            .map((calendar) => calendar?.name)
            .join(
              ', '
            )} can no longer be reached. Remove them from the panel to save.`
        });
      }

      if (isRoundRobinMethod(method)) {
        const error = validateParticipantsForMethod(
          method,
          (value ?? []).map(() => ({ isOrganizer: false })),
          this.options?.context?.unresolvedMemberCount ?? 0
        );

        return error ? this.createError({ message: error }) : true;
      }

      const ownerId: string | undefined = this.parent.owner;

      if (!ownerId) {
        return this.createError({
          message: 'Mark one interviewer as the organiser'
        });
      }

      const organizer = userCalendars.find(
        (calendar) => calendar.id === ownerId
      );

      const blockedReason = organizer
        ? getTemplateEditBlockedReason({
            availabilityMethod: method,
            organizerUserCalendar: toEditableOrganizer({ calendar: organizer })
          })
        : null;

      return blockedReason
        ? this.createError({ message: blockedReason })
        : true;
    }),
  mondayAvailability: meetingAvailabilitySchema.test(
    'at-least-one-day',
    'The bookable window has no hours, so no time can ever be booked',
    function () {
      return AVAILABILITY_FIELDS.some((field) =>
        (this.parent[field] ?? []).some(
          (range: unknown[] | undefined) => range?.length === 2
        )
      );
    }
  ),
  tuesdayAvailability: meetingAvailabilitySchema,
  wednesdayAvailability: meetingAvailabilitySchema,
  thursdayAvailability: meetingAvailabilitySchema,
  fridayAvailability: meetingAvailabilitySchema,
  saturdayAvailability: meetingAvailabilitySchema,
  sundayAvailability: meetingAvailabilitySchema,
  timeZone: Yup.string().required(),
  sendMeetingReminderValue: meetingTimeValueSchema,
  sendMeetingReminderUnit: meetingTimeUnitSchema,
  bufferTimeValue: nonNegativeNumberSchema
    .required('Value is required')
    .test('buffer-max', 'Buffer must be at most 120 minutes', function (value) {
      const valueInMinutes = convertTimeUnitValuesToMinutes(
        value,
        this.parent.bufferTimeUnit
      );
      return valueInMinutes <= 120;
    })
    .test(
      'buffer-multiple-of-5',
      'Buffer must be multiple of 5 minutes',
      function (value) {
        const valueInMinutes = convertTimeUnitValuesToMinutes(
          value,
          this.parent.bufferTimeUnit
        );
        return (valueInMinutes ?? 0) % 5 === 0;
      }
    ),
  bufferTimeUnit: meetingTimeUnitSchema,
  // calendar-service accepts 0 (no notice required), so rejecting it here would
  // block saving a template the API happily created.
  noticePeriodValue: nonNegativeNumberSchema.required('Value is required'),
  noticePeriodUnit: meetingTimeUnitSchema,
  bookBeforeValue: meetingTimeValueSchema.test(
    'window-past-notice',
    'Candidates must be able to book further ahead than the notice period',
    function (value) {
      const noticeMinutes = convertTimeUnitValuesToMinutes(
        this.parent.noticePeriodValue,
        this.parent.noticePeriodUnit
      );

      // A cleared field casts to NaN, which `?? 0` does not catch.
      if (!Number.isFinite(noticeMinutes)) {
        return true;
      }

      return (value ?? 0) * 1440 > (noticeMinutes as number);
    }
  ),
  // Hidden fields are never registered, so each rule must check visibility or it
  // blocks submit against nothing on screen.
  requiredHours: numberSchema
    .transform((value, original) =>
      original === '' || original === null ? undefined : value
    )
    .optional()
    .test('required-with-virtual-participant', function (value) {
      const userCalendars: UseUserCalendarReturnData =
        this.options?.context?.userCalendars ?? [];

      const { hasVirtualParticipant } = getParticipantStats(
        this.parent.owner,
        this.parent.participants,
        userCalendars
      );

      if (!hasVirtualParticipant) {
        return true;
      }

      if (value === undefined) {
        return this.createError({ message: 'Enter a minimum number of hours' });
      }

      if (!Number.isInteger(value)) {
        return this.createError({ message: 'Number must be a whole number' });
      }

      if (value < 1) {
        return this.createError({
          message: 'Minimum availability must be 1 hour or more'
        });
      }

      return value > 168
        ? this.createError({ message: 'Maximum availability is 168 hours' })
        : true;
    })
}).required();

export type CalendarMeetingTemplateFormData = Yup.InferType<
  typeof calendarMeetingTemplateSchema
>;
